import React, { useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Select from "react-select";

import NavbarMob from "./NavbarMob";
import Navbar from "./Navbar";
import Navbar2 from "./Navbar2";
import Navbar2Mob from "./Navbar2Mob";
import Footer from "./Footer";
import filterIcon from "../images/search/Filter.png";
import vector from "../images/home/Vector.png";

function SearchedJobs() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Prioritize URL (searchParams), fallback to state (for backward compatibility or direct navigation)
  const initialJob = searchParams.get("job") || location.state?.job || "";
  const initialLocation = searchParams.get("location") || location.state?.location || "";

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const navigate = useNavigate();

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // ── Data & Filter States ───────────────────────────────────────
  const [allJobs, setAllJobs] = useState([]);
  // removed pagination; keep placeholder to avoid stale references
  // eslint-disable-next-line no-unused-vars
  const [visibleCount, setVisibleCount] = useState(undefined);

  // Search & Filter controls
  const [searchTitle, setSearchTitle] = useState("");
  const [searchAddress, setSearchAddress] = useState("");
  const [selectedJob, setSelectedJob] = useState(initialJob || null);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation || null);
  const [selectedJobType, setSelectedJobType] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedFood, setSelectedFood] = useState(null);

  // Sync state with URL params
  useEffect(() => {
    const jobParam = searchParams.get("job") || "";
    const locParam = searchParams.get("location") || "";
    setSelectedJob(jobParam || null);
    setSelectedLocation(locParam || null);
  }, [searchParams]);

  // Dropdown options (generated from real data)
  const [jobOptions, setJobOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const [showFilters, setShowFilters] = useState(false);

  const customerType = sessionStorage.getItem("customerType");
  const selectedPlan = sessionStorage.getItem("selectedPlan");

  // ── Load all enabled jobs once ────────────────────────────────
  useEffect(() => {
    const fetchAllJobs = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/getjobposts`);
        if (!res.ok) throw new Error("Failed to load jobs");
        const data = await res.json();

        // Only enabled jobs
        const enabled = data.filter((job) => job.enable === "on");
        setAllJobs(enabled);
      } catch (err) {
        console.error("Error loading jobs:", err);
      }
    };

    fetchAllJobs();
  }, [apiBaseUrl]);

  // ── Generate dropdown options from loaded data ────────────────
  useEffect(() => {
    if (!allJobs.length) return;

    const uniqueJobs = [...new Set(allJobs.map((j) => j.job?.trim()))]
      .filter(Boolean)
      .sort()
      .map((val) => ({ value: val, label: val }));

    // Parse multi-location strings: "Kochi, Trivandrum" -> ["Kochi", "Trivandrum"]
    const allLocations = allJobs.flatMap(j =>
      j.location ? j.location.split(',').map(l => l.trim()) : []
    );

    const uniqueLocations = [...new Set(allLocations)]
      .filter(Boolean)
      .sort()
      .map((val) => ({ value: val, label: val }));

    setJobOptions(uniqueJobs);
    setLocationOptions(uniqueLocations);
  }, [allJobs]);

  // ── Main client-side filtering (very similar to EnableJobPost style) ──
  const filteredJobs = useMemo(() => {
    return allJobs.filter((job) => {
      // Job match: allow case-insensitive substring match across job & job_title
      if (selectedJob) {
        const needle = selectedJob.trim().toLowerCase();
        const jobName = (job.job || "").trim().toLowerCase();
        const jobTitle = (job.job_title || "").trim().toLowerCase();
        if (
          needle &&
          !jobName.includes(needle) &&
          !jobTitle.includes(needle)
        )
          return false;
      }

      // Location match: only filter when a location is selected
      if (selectedLocation) {
        const needle = selectedLocation.trim().toLowerCase();
        const locationTokens = (job.location || "")
          .split(",")
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);
        const addressTokens = (job.address || "")
          .split(",")
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);
        const matchesLocation =
          locationTokens.includes(needle) ||
          addressTokens.includes(needle) ||
          (job.location || "").trim().toLowerCase() === needle ||
          (job.address || "").trim().toLowerCase() === needle;

        if (!matchesLocation) return false;
      }

      // Job type
      if (selectedJobType && selectedJobType !== "any") {
        if (!job.job_type || job.job_type.toLowerCase() !== selectedJobType.toLowerCase())
          return false;
      }

      // Gender
      if (selectedGender) {
        if (selectedGender === "MALE/FEMALE") {
          if (job.gender_type !== "MALE/FEMALE") return false;
        } else {
          if (job.gender_type?.toUpperCase() !== selectedGender.toUpperCase()) return false;
        }
      }

      // Food & Accommodation
      if (selectedFood) {
        const hasFood = !!job.food;
        const hasAccom = !!job.accommodation;

        if (selectedFood === "no" && (hasFood || hasAccom)) return false;
        if (selectedFood === "yes" && !hasFood && !hasAccom) return false;
        if (selectedFood === "Accomodationonly" && (!hasAccom || hasFood)) return false;
        if (selectedFood === "Foodonly" && (!hasFood || hasAccom)) return false;
      }

      // Free text - Job Title
      if (searchTitle.trim()) {
        if (!job.job_title?.toLowerCase().includes(searchTitle.trim().toLowerCase()))
          return false;
      }

      // Free text - Address / Area
      if (searchAddress.trim()) {
        const search = searchAddress.trim().toLowerCase();
        if (
          !job.address?.toLowerCase().includes(search) &&
          !job.location?.toLowerCase().includes(search)
        )
          return false;
      }

      return true;
    });
  }, [
    allJobs,
    selectedJob,
    selectedLocation,
    selectedJobType,
    selectedGender,
    selectedFood,
    searchTitle,
    searchAddress,
  ]);

  // ── Handlers ───────────────────────────────────────────────────
  const clearAll = () => {
    setSelectedJob(null);
    setSelectedLocation(null);
    setSelectedJobType(null);
    setSelectedGender(null);
    setSelectedFood(null);
    setSearchTitle("");
    setSearchAddress("");
  };

  const goToDetails = (id) => {
    const realId = id.manualJobID && id.manualJobID !== "0" ? id.manualJobID : id.job_id;
    navigate("/details", { state: { jobId: realId } });
  };

  const formatJobTitle = (title = "") => {
    const lowers = ["at", "in", "of", "for", "to", "and", "on", "by", "with"];
    return title
      .split(" ")
      .map((w, i) =>
        lowers.includes(w.toLowerCase()) && i !== 0
          ? w.toLowerCase()
          : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
      )
      .join(" ");
  };

  const selectStyle = {
    control: (base) => ({
      ...base,
      height: "43px",
      minHeight: "43px",
      borderRadius: "5px",
      border: "2px solid #D7D7D7",
      paddingLeft: "4px",
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };

  // ── RENDER ─────────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-screen">
      {isMobile ? <NavbarMob /> : <Navbar />}
      <div className="hidden md:block">
        <Navbar2 />
      </div>
      <div className="md:hidden">
        <Navbar2Mob />
      </div>

      <div className="flex w-full px-2 bg-[#eeebeb] lg:flex-row flex-col gap-3 py-6">
        {/* Filter Sidebar - Admin only */}
        {(customerType === "mainAdmin" || customerType === "admin") && (
          <div className="lg:w-[25%] w-full rounded-[10px] flex flex-col bg-white">
            <div className="w-full h-[50px] bg-white rounded-t-[10px] px-5 flex justify-between items-center border-b-2 border-[#d2d0d0]">
              <div className="flex items-center gap-2">
                <img
                  src={filterIcon}
                  alt="filter"
                  className="cursor-pointer w-6"
                  onClick={() => setShowFilters(!showFilters)}
                />
                <span className="text-lg font-medium">Search Job</span>
              </div>
              <span
                className="text-base font-medium cursor-pointer hover:text-[#E22E37]"
                onClick={clearAll}
              >
                clear all
              </span>
            </div>

            {showFilters && (
              <div className="p-5 flex flex-col gap-5">
                <div>
                  <label className="block mb-1 font-medium">Job</label>
                  <Select
                    options={jobOptions}
                    value={jobOptions.find((o) => o.value === selectedJob) || null}
                    onChange={(opt) => {
                      const val = opt?.value ?? "";
                      setSelectedJob(val);
                      setSearchParams(prev => {
                        const newParams = new URLSearchParams(prev);
                        if (val) newParams.set("job", val);
                        else newParams.delete("job");
                        return newParams;
                      });
                    }}
                    placeholder="Select job"
                    isClearable
                    styles={selectStyle}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium">Location</label>
                  <Select
                    options={locationOptions}
                    value={locationOptions.find((o) => o.value === selectedLocation) || null}
                    onChange={(opt) => {
                      const val = opt?.value ?? "";
                      setSelectedLocation(val);
                      setSearchParams(prev => {
                        const newParams = new URLSearchParams(prev);
                        if (val) newParams.set("location", val);
                        else newParams.delete("location");
                        return newParams;
                      });
                    }}
                    placeholder="Select location"
                    isClearable
                    styles={selectStyle}
                  />
                </div>

                {/* Job Type */}
                <div>
                  <label className="block mb-1 font-medium">Job Type</label>
                  <Select
                    options={[
                      { value: "any", label: "Any" },
                      { value: "fulltime", label: "Full-time" },
                      { value: "parttime", label: "Part-time" },
                      { value: "remote", label: "Remote/Work at Home" },
                    ]}
                    value={
                      selectedJobType
                        ? {
                          value: selectedJobType,
                          label: selectedJobType.charAt(0).toUpperCase() + selectedJobType.slice(1),
                        }
                        : null
                    }
                    onChange={(opt) => setSelectedJobType(opt?.value ?? null)}
                    isClearable
                    styles={selectStyle}
                  />
                </div>

                {/* Gender */}
                <div>
                  <label className="block mb-1 font-medium">Gender</label>
                  <Select
                    options={[
                      { value: "male", label: "Male" },
                      { value: "female", label: "Female" },
                      { value: "MALE/FEMALE", label: "Both" },
                    ]}
                    value={
                      selectedGender
                        ? {
                          value: selectedGender,
                          label: selectedGender === "MALE/FEMALE" ? "Both" : selectedGender,
                        }
                        : null
                    }
                    onChange={(opt) => setSelectedGender(opt?.value ?? null)}
                    isClearable
                    styles={selectStyle}
                  />
                </div>

                {/* Food & Accom */}
                <div>
                  <label className="block mb-1 font-medium">Food & Accommodation</label>
                  <Select
                    options={[
                      { value: "no", label: "No" },
                      { value: "yes", label: "Yes" },
                      { value: "Accomodationonly", label: "Accommodation Only" },
                      { value: "Foodonly", label: "Food Only" },
                    ]}
                    value={
                      selectedFood
                        ? {
                          value: selectedFood,
                          label:
                            selectedFood === "Accomodationonly"
                              ? "Accommodation Only"
                              : selectedFood === "Foodonly"
                                ? "Food Only"
                                : selectedFood,
                        }
                        : null
                    }
                    onChange={(opt) => setSelectedFood(opt?.value ?? null)}
                    isClearable
                    styles={selectStyle}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Main Content ─────────────────────────────────────────────── */}
        <div className="w-full flex flex-col gap-6 lg:px-6 px-3">
          {/* Quick search for candidates */}
          {/* Quick search for candidates with Plan */}
          {customerType === "candidate" && selectedPlan !== "null" && (
            <div className="bg-white rounded-[12px] p-5 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Search by Job Name..."
                  value={searchTitle}
                  onChange={(e) => setSearchTitle(e.target.value)}
                  className="w-full px-5 py-3 border-2 border-[#C5C5C5] rounded-[10px] focus:outline-none focus:border-[#E22E37]"
                />
                <input
                  type="text"
                  placeholder="Search by Location / Address / Area..."
                  value={searchAddress}
                  onChange={(e) => setSearchAddress(e.target.value)}
                  className="w-full px-5 py-3 border-2 border-[#C5C5C5] rounded-[10px] focus:outline-none focus:border-[#E22E37]"
                />
              </div>
            </div>
          )}

          {/* Job Count & Clear Filters - Visible to ALL */}
          <div className="flex justify-between items-center px-2">
            <p className="text-sm font-semibold text-gray-700">
              Showing <span className="text-[#E22E37]">{filteredJobs.length}</span> of <span className="text-black">{allJobs.length}</span> job(s)
            </p>
            {(searchTitle.trim() || searchAddress.trim()) && (
              <button
                onClick={() => {
                  setSearchTitle("");
                  setSearchAddress("");
                }}
                className="text-[#E22E37] underline hover:text-red-700 text-sm font-medium"
              >
                Clear Search
              </button>
            )}
          </div>

          {/* Job Cards */}
          <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
            {filteredJobs.length === 0 ? (
              <div className="col-span-full text-center py-20 text-gray-600 text-xl">
                No jobs found matching your criteria.
              </div>
            ) : (
              filteredJobs.map((job) => (
                <div
                  key={job.job_id}
                  className="h-[420px] border-2 border-[#C5C5C5] rounded-[10px] flex flex-col overflow-hidden bg-white"
                >
                  <div className="h-[25%] bg-white border-b-2 border-[#C5C5C5] flex flex-col justify-center items-center px-3">
                    <h3 className="text-lg font-bold text-center line-clamp-2">
                      {formatJobTitle(job.job_title || job.job || "")}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <img src={vector} alt="loc" className="w-4 h-4" />
                      <span>{job.location || "—"}</span>
                    </div>
                  </div>

                  <div className="w-full h-[80%] flex flex-col bg-white p-4">
                    <div className="flex-1 grid grid-cols-3 gap-x-1 gap-y-0 text-base">
                      <div className="font-display font-[600] col-span-1 text-left">
                        JOB ID
                      </div>
                      <div className="font-[500] text-left break-words col-span-2">
                        :{" "}
                        {job.manualJobID && job.manualJobID !== "0"
                          ? job.manualJobID
                          : job.job_id}
                      </div>

                      <div className="font-display font-[600] col-span-1 text-left">
                        JOB
                      </div>
                      <div className="font-[500] text-left break-words col-span-2">
                        : {job.job}
                      </div>

                      {/* <div className="font-display font-[600] col-span-1 text-left">
                        JOB TYPE
                      </div>
                      <div className="font-[500] text-left col-span-2">
                        : {job.job_type}
                      </div> */}

                      <div className="font-display font-[600] col-span-1 text-left">
                        BOYS / GIRLS
                      </div>
                      <div className="font-[500] text-left col-span-2">
                        :{" "}
                        {job.gender_type === "MALE"
                          ? "BOYS"
                          : job.gender_type === "FEMALE"
                            ? "GIRLS"
                            : job.gender_type === "MALE/FEMALE"
                              ? "BOYS/GIRLS"
                              : job.gender_type}
                      </div>

                      <div className="font-display font-[600] col-span-1 text-left">
                        Salary
                      </div>
                      <div className="font-[500] text-left col-span-2">
                        :{" "}
                        {job.min_salary > 0 && job.max_salary > 0
                          ? `${job.min_salary} - ${job.max_salary}`
                          : job.min_salary > 0
                            ? job.min_salary
                            : job.max_salary > 0
                              ? job.max_salary
                              : job.salaryType || "Negotiable"}
                      </div>

                      <div className="font-display font-[600] col-span-1 text-left">
                        Qualification
                      </div>
                      <div className="font-[500] text-left col-span-2">
                        : {job.qualification ? job.qualification : ""}
                      </div>
                    </div>

                    <div className="flex justify-center gap-4 mt-6">
                      <button
                        onClick={() => goToDetails(job)}
                        className="w-[120px] h-[38px] bg-black text-white rounded-[10px] font-semibold hover:bg-[#E22E37] transition"
                      >
                        Apply Now
                      </button>
                      <button
                        onClick={() => goToDetails(job)}
                        className="w-[120px] h-[38px] bg-black text-white rounded-[10px] font-semibold hover:bg-[#E22E37] transition"
                      >
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}

export default SearchedJobs;
